import { LinkCellRenderer } from "@/controls/ag-grid/components/ButtonCellRenderer";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { ColDef, ICellRendererParams } from "ag-grid-community";

export const useTableData = () => {
	const columnData: ColDef[] = [
		{
			headerName: "Heat",
			field: "HeatName",
			flex: 1,
			minWidth: 70,
			maxWidth: 70,
			sortable: true,
			cellRenderer: (params: any) => {
				return LinkCellRenderer({
					...params,
					link: `/caster-data-entry/${params.data.ProcessId}`,
				});
			},
		},
		{
			headerName: "Last End",
			field: "LastEnd",
			minWidth: 100,
			valueGetter: (params) =>
				params.data.LastEnd
					? dxtToLocalServerTime(params.data.LastEnd, "MMM dd - HH:mm:ss")
					: "",
			flex: 1,
			cellStyle: {
				textAlign: "center",
			},
			sortable: true,
		},
		{
			headerName: "Prev Interval [min]",
			field: "PrevInterval",
			minWidth: 120,
			valueGetter: (params) => {
				if (params.data.LastEnd && params.data.Start) {
					const lastEnd = new Date(params.data.LastEnd);
					const start = new Date(params.data.Start);
					const diffMs = start.getTime() - lastEnd.getTime();
					const diffMinutes = Math.round(diffMs / (1000 * 60));
					return diffMinutes;
				}
				return "";
			},
			flex: 1,
			cellStyle: {
				textAlign: "center",
			},
			sortable: true,
		},
		{
			headerName: "Start",
			field: "Start",
			minWidth: 100,
			valueGetter: (params) =>
				params.data.Start
					? dxtToLocalServerTime(params.data.Start, "MMM dd - HH:mm:ss")
					: "",
			flex: 1,
			cellStyle: {
				textAlign: "center",
			},
			sortable: true,
		},
		{
			headerName: "Duration [min]",
			field: "Duration",
			minWidth: 120,
			valueGetter: (params) => {
				if (params.data.Start && params.data.End) {
					const start = new Date(params.data.Start);
					const end = new Date(params.data.End);
					const diffMs = end.getTime() - start.getTime();
					const diffMinutes = Math.round(diffMs / (1000 * 60));
					return diffMinutes;
				}
				return "";
			},
			flex: 1,
			cellStyle: {
				textAlign: "center",
			},
			sortable: true,
		},
		{
			headerName: "End",
			field: "End",
			minWidth: 100,
			valueGetter: (params) =>
				params.data.End
					? dxtToLocalServerTime(params.data.End, "MMM dd - HH:mm:ss")
					: "",
			flex: 1,
			cellStyle: {
				textAlign: "center",
			},
			sortable: true,
		},
		{
			headerName: "Next Interval [min]",
			field: "NextInterval",
			minWidth: 120,
			valueGetter: (params) => {
				if (params.data.End && params.data.NextStart) {
					const end = new Date(params.data.End);
					const nextStart = new Date(params.data.NextStart);
					const diffMs = nextStart.getTime() - end.getTime();
					const diffMinutes = Math.round(diffMs / (1000 * 60));
					return diffMinutes;
				}
				return "";
			},
			flex: 1,
			cellStyle: {
				textAlign: "center",
			},
			sortable: true,
		},
		{
			headerName: "Next Start",
			field: "NextStart",
			minWidth: 100,
			valueGetter: (params) =>
				params.data.NextStart
					? dxtToLocalServerTime(params.data.NextStart, "MMM dd - HH:mm:ss")
					: "",
			flex: 1,
			cellStyle: {
				textAlign: "center",
			},
			sortable: true,
		},
		{
			headerName: "Shift",
			field: "Shift",
			minWidth: 80,
			maxWidth: 80,
			cellStyle: {
				textAlign: "center",
			},
			cellRenderer: (params: ICellRendererParams) => {
				return (
					<div className={`custom-chip shift-chip-${params.value}`}>
						{params.value}
					</div>
				);
			},
			cellClass: (params) => {
				return `shift-chip-${params.value}`;
			},
			flex: 1,
			sortable: true,
		},
		{
			headerName: "Crew",
			field: "Crew",
			minWidth: 80,
			maxWidth: 80,
			cellStyle: {
				textAlign: "center",
			},
			cellRenderer: (params: ICellRendererParams) => {
				return (
					<div className={`custom-chip crew-chip-${params.value}`}>
						{params.value}
					</div>
				);
			},
			cellClass: (params) => {
				return `crew-chip-${params.value}`;
			},
			flex: 1,
			sortable: true,
		},
	];

	const defaultColDef: ColDef = {
		flex: 1,
		resizable: true,
	};

	return { columnData, defaultColDef };
};

export const useGroupedTableData = () => {
	const columnData: ColDef[] = [
		{
			headerName: "Year",
			field: "Year",
			minWidth: 80,
			flex: 1,
			cellStyle: {
				textAlign: "center",
				fontWeight: "bold",
			},
			sortable: true,
		},
		{
			headerName: "Month",
			field: "MonthName",
			minWidth: 100,
			flex: 1,
			cellStyle: {
				textAlign: "center",
				fontWeight: "bold",
			},
			sortable: true,
		},
		{
			headerName: "Shift D",
			field: "ShiftD",
			minWidth: 80,
			flex: 1,
			cellStyle: {
				textAlign: "center",
			},
			sortable: true,
		},
		{
			headerName: "Shift N",
			field: "ShiftN",
			minWidth: 80,
			flex: 1,
			cellStyle: {
				textAlign: "center",
			},
			sortable: true,
		},
		{
			headerName: "Crew A",
			field: "CrewA",
			minWidth: 80,
			flex: 1,
			cellStyle: {
				textAlign: "center",
			},

			sortable: true,
		},
		{
			headerName: "Crew B",
			field: "CrewB",
			minWidth: 80,
			flex: 1,
			cellStyle: {
				textAlign: "center",
			},

			sortable: true,
		},
		{
			headerName: "Crew C",
			field: "CrewC",
			minWidth: 80,
			flex: 1,
			cellStyle: {
				textAlign: "center",
			},

			sortable: true,
		},
		{
			headerName: "Crew D",
			field: "CrewD",
			minWidth: 80,
			flex: 1,
			cellStyle: {
				textAlign: "center",
			},

			sortable: true,
		},
		{
			headerName: "Total",
			field: "TotalFailedStartups",
			minWidth: 80,
			flex: 1,
			cellStyle: {
				textAlign: "center",
				fontWeight: "bold",
				backgroundColor: "#f0f8ff",
			},
			sortable: true,
		},
	];

	const defaultColDef: ColDef = {
		flex: 1,
		resizable: true,
	};

	return { columnData, defaultColDef };
};
